import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import {
  buildEvent,
  getCoverImage,
  getSizesForResponsiveImage,
  setCovetItemSizeVariables,
  getDateFromTimestamp,
  AppStoreType,
  AppStateType,
  getSize
} from "../../utils/utils";
import Isotope from "isotope-layout";
import { GalleryLoaderService } from "../../services/gallery-loader-service";
import { isWidgetDisabled } from "../../store/getters";
import { setPopupItems, setPopupItemId } from "../../store/popup";
import { Settings, Unsubscribe } from "../../utils/types";

@Component({
  tag: "covet-pics-gallery-grid",
  styleUrl: "covet-pics-gallery-grid.scss",
})

export class CovetPicsGalleryGrid {
  @Element() el: HTMLCovetPicsGalleryGridElement;

  /**
   * Isotope - from isotope-layout
   */
  private iso;

  /**
   * CovetPicsPopup element
   * we don't render() popup in a standard way
   * we inject it at the end </body>
   * because popup needs to be on top all elements
   */
  private popup: HTMLCovetPicsPopupElement;

  /**
   * CovetPicsUpload element
   */
  private upload: HTMLCovetPicsUploadElement;

  /**
   * How many images per page (Number of photos at once)
   */
  @Prop() imagesToShow: number = 9;

  /**
   * array of items to be showed in grid
   */
  @Prop() items: Array<any> = [];

  /**
   * array of breakdown for header
   */
  @Prop() breakdown: Array<any> = [];
  
  /**
   * product-title in header
   */
  @Prop() productTitle: string = "";

  /**
   * rating
   */
  @Prop() rating: number = 0;

  /*
   * Gallery filters
   */
  @Prop() filtersEnabled: boolean = false;
  @Prop() filters: object = {};

  /**
   * Instance of GalleryLoaderService to handle API interaction
   */
  @Prop() loader: GalleryLoaderService;

  /**
   * App store
   */
  @Prop() appStore: AppStoreType;

  // App state;
  private appState: AppStateType;
  private unsubscribe: Unsubscribe;
  private settings: Settings;

  /**
   * shared item id to open popup or upload widget
  */
  @Prop() sharedItemId: string; 

  @State() isLoading: boolean = false;
  @State() showSort: boolean = false;
  @State() isGridDisabled: boolean = false;
  @State() isDesktopScreen: boolean;

  /**
   * emitted when "load next page" is initiated
   */
  @Event() nextPage: EventEmitter;

  /**
   *  Emitted when Direct Buy is closed
  */
  @Event() sortUpdated: EventEmitter;

  /**
   *  Emitted when filters updated
  */
  @Event() filtersUpdated: EventEmitter;

  private selectedItemIndex: number = null;
  private layout: string; // short var name

  /**
   * after CovetGalleryItem image is loaded, refresh layout
   */
  @Listen("imageLoaded")
  imagesLoadedHandler() {
    if (typeof this.iso !== "undefined") { this.iso.arrange(); }
  }

  @Listen("itemClicked")
  itemClickedHandler(event: CustomEvent) {
    const source = event.detail.itemSource;
    const itemId = event.detail.itemId;

    if (source === "upload_widget") {
      this.upload.openUpload();
    } else {
      const index = this.items.findIndex(item => item.id && item.id.toString() === itemId);

      this.selectedItemIndex = index;
      this.appStore.dispatch(setPopupItemId(parseInt(itemId)));
      this.popup.openPopup(itemId);
      this.preloadNextPage(event);
    }
  }

  @Listen("previousPopupEvent", { target: "window" })
  prevPopupHandler() {
    if (this.selectedItemIndex > 0) { --this.selectedItemIndex; }
  }

  @Listen("nextPopupEvent", { target: "window" })
  nextPopupHandler(event: CustomEvent) {
    if (this.selectedItemIndex < this.lastItemIndex) {
      ++this.selectedItemIndex;
      this.preloadNextPage(event);
    }
  }

  @Listen("elementResize", { target: "window" })
  elementResizeHandler() {
    if (typeof this.iso !== "undefined") {
      this.iso.arrange();
    }
  }

  @Watch("items")
  itemsChangeHandler(newValue: Array<any>) {
    this.appStore.dispatch(setPopupItems(newValue));
  }

  componentWillLoad() {
    if (this.appStore !== undefined) { this.initStore(); } // TODO temp check for pass spec.test, issue props function in stencil tests
    this.initPopup();
    this.initFilters();
    this.initUploadWidget();
  }

  componentDidLoad() {
    if (this.layout === "collage") this.initIsotope();
    this.emitGalleryReadyEvent();
    this.bindCustomUploadWidgetButtons();
    this.checkAndOpenPopup();      
    if (this.settings.pagination === "infinite_scroll") {
      window.addEventListener("scroll", this.scrollHandler, { passive: true });
    }
  }

  componentDidUpdate() {
    if (this.layout === "grid") return true;
    this.iso.reloadItems();
    this.iso.arrange();
  }

  disconnectedCallback() {
    this.unsubscribe();
  }

  private initStore() {
    this.appState = this.appStore.getState();
    this.settings = this.appState.settings;
    this.layout = this.appState.settings.display_type;
    this.isDesktopScreen = this.appState.size.isDesktopScreen; 
    this.unsubscribe = this.appStore.subscribe(() => {
      this.appState = this.appStore.getState();
      this.settings = this.appState.settings;
      this.layout = this.appState.settings.display_type;
      this.isDesktopScreen = this.appState.size.isDesktopScreen; 
      this.isGridDisabled = isWidgetDisabled(this.appState);
    });
  }

  private get lastItemIndex() {
    return this.items.length - 1;
  }

  private isHighlighted(index) {
    if (this.layout === "collage") return false;
    if (!this.settings.gallery_highlight) return false;
    if (this.settings.gallery_highlight_every === 0) return false;

    let position = (index + 1) % this.imagesToShow;
    if (position === 0) position = this.imagesToShow;
    const start = this.settings.gallery_highlight_every_start + 1;
    const every = this.settings.gallery_highlight_every;

    if (every < 0) return false;
    if (position < start) return false;

    const highlightOffset = (position - start);
    return (highlightOffset % every) === 0;
  }

  private bindCustomUploadWidgetButtons() {
    const selector = this.settings.customer_photo_upload_button_selector;

    if (selector !== "") {
      const nodes = document.querySelectorAll(selector) as NodeListOf<HTMLElement>;
      nodes.forEach(node => {
        node.addEventListener("click", () => this.upload.openUpload());
      });
    }
  }

  private initPopup() {
    if (this.appState.preview.mode) { document.body.querySelectorAll("covet-pics-popup").forEach(node => node.remove()); }

    this.appStore.dispatch(setPopupItems(this.items)); // TODO move this to main component (double init in grid and slider)
    this.popup = document.createElement("covet-pics-popup");
    this.popup.appStore = this.appStore;
    document.body.appendChild(this.popup);
  }

  private initUploadWidget() {
    if (this.appState.preview.mode) { document.querySelectorAll("covet-pics-upload").forEach(uploadWidget => uploadWidget.remove()); }

    if (this.settings.customer_photo_item_position !== null || this.settings.customer_photo_upload_button_selector !== "" || this.settings.show_header) {
      this.upload = document.createElement("covet-pics-upload");
      this.upload.appStore = this.appStore;
      document.body.appendChild(this.upload);
    }
  }

  private preloadNextPage(event: CustomEvent) {
    if (this.lastItemIndex === this.selectedItemIndex && this.settings.pagination !== "none") {
      this.nextPage.emit(event);
    }
  }

  private checkAndOpenPopup() {
    if (this.sharedItemId === "none") return false;
    if (this.sharedItemId === "upload_widget") return this.upload.openUpload();

    return this.popup.openPopup(this.sharedItemId);
  }

  private initIsotope() {
    const galleryNode = this.el.querySelector(".gallery");

    if (galleryNode !== null) {
      this.iso = new Isotope(galleryNode, {
        itemSelector: ".gallery .collage-item",
        percentPosition: true,
        stagger: 10,
        layoutMode: "masonry",
        masonry: {
          columnWidth: ".grid-sizer",
          gutter: ".gutter-sizer",
          horizontalOrder: false
        }
      });
    }
  }

  private emitGalleryReadyEvent() {
    const galleryReadyEvent = buildEvent("galleryReady:covetPics", { galleryId: this.appState.widgetId.galleryId, itemsCount: this.items.length, el: this.el });
    document.dispatchEvent(galleryReadyEvent);
  }

  private setLoadMoreButtonVariables() {
    return {
      "--covet-pics-load-more-border": this.settings.load_more_border_width.toString() + "px",
      "--covet-pics-load-more-color": this.settings.load_more_button_color,
      "--covet-pics-load-more-font-size": this.settings.load_more_font_size.toString() + "px",
      "--covet-pics-load-more-font-style": this.settings.load_more_font_style,
      "--covet-pics-load-more-padding": this.settings.load_more_padding.toString() + "px"
    };
  }

  private renderLoadMoreIcon() {
    switch (this.settings.load_more_button_style) {
    case "btn_style_3":
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14"><g fill="none" fill-rule="evenodd"><path d="M-3-3h20v20H-3z" /><path class="dynamic-stroke" stroke="#919EAB" d="M11.125 5.542L7 9.666 2.875 5.542" /></g></svg>
      );

    case "btn_style_4":
      return (
        <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><path d="M17.6 0v14.399L32 14.4v3.2H17.6V32h-3.2l-.001-14.4H0v-3.2l14.399-.001L14.4 0h3.2z" fill="#101010" fill-rule="evenodd" /></svg>
      );

    case "btn_style_5":
      return (
        <svg viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg"><path d="M22 0c12.15 0 22 9.85 22 22s-9.85 22-22 22S0 34.15 0 22 9.85 0 22 0zm1.1 13h-2.2v7.899L13 20.9v2.2l7.9-.001V31h2.2v-7.901l7.9.001v-2.2l-7.9-.001V13z" fill="#101010" fill-rule="evenodd" /></svg>
      );
    }
  }

  private rederLoadMoreButton() {
    if (this.settings.pagination === "button" &&
      !this.isLoading &&
      this.loader.totalPages > this.loader.page &&
      this.items.length > 0) {
      return (
        <div class='load-more' style={this.setLoadMoreButtonVariables()} onClick={this.loadMoreClickedHandler}>
          <button class={"btn-load-more " + this.settings.load_more_button_style} aria-label="load more items" disabled={this.isGridDisabled} 
            aria-hidden={`${this.isGridDisabled}`} type="button">
            <span class="text">{this.settings.load_more_text}</span>
            <span class="icon" aria-hidden="true">{this.renderLoadMoreIcon()}</span>
          </button>
        </div>
      );
    }
  }

  private renderLoadingAnimation() {
    return (
      <div class={`loading-animation ${this.loader.isLoading ? "show" : ""}`}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid" role="img">
          <title>loading more animation</title>
          <circle cx="84" cy="50" r="1.23" fill="#919eab" class="dot-1"><animate attributeName="r" values="10;0;0;0;0" keyTimes="0;0.25;0.5;0.75;1" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" calcMode="spline" dur="1.9s" repeatCount="indefinite" begin="0s" /><animate attributeName="cx" values="84;84;84;84;84" keyTimes="0;0.25;0.5;0.75;1" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" calcMode="spline" dur="1.9s" repeatCount="indefinite" begin="0s" /></circle><circle cx="79.817" cy="50" r="10" fill="rgba(0, 0, 0, 0.6)" class="dot-2"><animate attributeName="r" values="0;10;10;10;0" keyTimes="0;0.25;0.5;0.75;1" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" calcMode="spline" dur="1.9s" repeatCount="indefinite" begin="-0.95s" /><animate attributeName="cx" values="16;16;50;84;84" keyTimes="0;0.25;0.5;0.75;1" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" calcMode="spline" dur="1.9s" repeatCount="indefinite" begin="-0.95s" /></circle><circle cx="45.817" cy="50" r="10" fill="rgba(0, 0, 0, 0.6)" class="dot-3"><animate attributeName="r" values="0;10;10;10;0" keyTimes="0;0.25;0.5;0.75;1" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" calcMode="spline" dur="1.9s" repeatCount="indefinite" begin="-0.475s" /><animate attributeName="cx" values="16;16;50;84;84" keyTimes="0;0.25;0.5;0.75;1" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" calcMode="spline" dur="1.9s" repeatCount="indefinite" begin="-0.475s" /></circle><circle cx="16" cy="50" r="8.77" fill="#919eab" class="dot-4"><animate attributeName="r" values="0;10;10;10;0" keyTimes="0;0.25;0.5;0.75;1" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" calcMode="spline" dur="1.9s" repeatCount="indefinite" begin="0s" /><animate attributeName="cx" values="16;16;50;84;84" keyTimes="0;0.25;0.5;0.75;1" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" calcMode="spline" dur="1.9s" repeatCount="indefinite" begin="0s" /></circle><circle cx="16" cy="50" r="0" fill="#919eab" class="dot-5"><animate attributeName="r" values="0;0;10;10;10" keyTimes="0;0.25;0.5;0.75;1" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" calcMode="spline" dur="1.9s" repeatCount="indefinite" begin="0s" /><animate attributeName="cx" values="16;16;16;50;84" keyTimes="0;0.25;0.5;0.75;1" keySplines="0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1;0 0.5 0.5 1" calcMode="spline" dur="1.9s" repeatCount="indefinite" begin="0s" /></circle>
        </svg>
      </div>
    );
  }

  /*
  * Filters
  */

  private initFilters() {
    const previewFilters = this.loader.previewSettings["filters"];

    this.loader.data.filters.list.forEach(f => this.filters[f] = false);

    // restore selected filters between preview reloads
    if (this.appState.preview.mode && typeof previewFilters !== "undefined") {
      previewFilters.forEach(activeFilter => this.filters[activeFilter] = true);
    }
  }

  private resetFilters() {
    Object.keys(this.filters).forEach(f => this.filters[f] = false);
    this.loader.setSelectedFilters([]);

    if (this.appState.preview.mode) { delete this.loader.previewSettings["filters"]; }
  }

  private fetchFilteredItems() {
    this.loader.setSelectedFilters(this.filtersSelected());
    this.filtersUpdated.emit();
  }

  private filtersSelected() {
    return Object.keys(this.filters).filter(f => this.filters[f]);
  }

  private filterChangedHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const filter = event.target.dataset.filter;

    if (!this.settings.multiple_filter_enabled) { this.resetFilters(); }

    this.filters[filter] = !this.filters[filter];
    this.fetchFilteredItems();
  }

  private filterResetHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.resetFilters();
    this.fetchFilteredItems();
  }

  private getFilterCSSVariables() {
    return {
      "--covet-pics-filter-font-weight": this.settings.tags_font_weight.toLowerCase(),
      "--covet-pics-filter-font-size": `${this.settings.tags_font_size}px`,
      "--covet-pics-filter-font-color": this.settings.tags_color,
      "--covet-pics-filter-padding": `${this.settings.tags_padding}%`,
    };
  }

  private renderFilters() {
    return (
      <div class="filters" style={this.getFilterCSSVariables()} aria-hidden={`${this.isGridDisabled}`}>
        <button
          tabindex="0"
          class="btn-filter"
          data-reset-filters="true"
          onClick={this.filterResetHandler}
          aria-label="reset filters"
          disabled={this.isGridDisabled}
          type="reset">
          {this.settings.tags_reset_button_text}
        </button>

        {Object.keys(this.filters).map((filter) =>
          <button
            tabindex="0"
            data-filter={filter}
            class={`btn-filter ${this.filters[filter] ? "selected" : ""}`}
            onClick={this.filterChangedHandler}
            aria-label={`filter ${filter}`}
            disabled={this.isGridDisabled}
            type="button">
            {filter}
          </button>
        )}
      </div>
    );
  }

  private loadMoreClickedHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.nextPage.emit(event);
  }

  private scrollHandler = (event) => {
    let elBottomPosition = getSize(this.el, "bottom");
    let toBottom = Math.max(elBottomPosition - this.appState.size.windowHeight, 0);

    if (toBottom < 300) { this.nextPage.emit(event); }
  }

  private itemImgAltTag(altTag: string, caption: string, idx: number, itemsLength: number, createdTime: string, layout: string) {
    if (altTag && altTag !== null) return altTag;

    return caption && caption.length > 0  
      ? `${caption}, ${layout}-item ${idx + 1}/${itemsLength}, post created ${getDateFromTimestamp(createdTime)}`
      : `${layout}-item ${idx + 1}/${itemsLength}, post created ${getDateFromTimestamp(createdTime)}`;
  }

  private renderStandardItem(item, idx: number, itemsLength: number) {
    const itemWidth = this.isDesktopScreen
      ? (100 - (this.settings.item_padding * (this.settings.gallery_photos_per_row - 1))) / this.settings.gallery_photos_per_row
      : (100 - (this.settings.item_mobile_padding * (this.settings.gallery_mobile_photos_per_row - 1))) / this.settings.gallery_mobile_photos_per_row;

    return (
      <covet-pics-gallery-item
        class={{ "grid-item": this.layout === "grid", "collage-item": this.layout === "collage" }}
        highlighted={this.isHighlighted(idx)}
        image-url={getCoverImage(item.cover_images.medium_resolution, item.images.medium_resolution)}
        image-high-resolution-url={getCoverImage(item.cover_images.high_resolution, item.images.high_resolution)}
        image-responsive-sizes={getSizesForResponsiveImage(this.settings.gallery_photos_per_row, this.settings.gallery_mobile_photos_per_row, this.isHighlighted(idx))}
        item-id={item.id}
        item-source={item.source}
        item-index={idx}
        caption={item.caption}
        item-type={item.data_type}
        links={item.links}
        url={item.url}
        rating={item.rating !== null ? item.rating : 0}
        user-name={item.username}
        item-width={itemWidth}
        created-time={item.created_time}
        role="listitem"
        aria-label={`${this.layout}-item ${idx + 1}/${itemsLength}, post created ${getDateFromTimestamp(item.created_time)}`}
        alt-tag={this.itemImgAltTag(item.alt_tag, item.caption, idx, itemsLength, item.created_time, this.layout)}
        appStore={this.appStore}
      >
      </covet-pics-gallery-item>);
  }

  private renderStandardUploadItem(item: any, idx: number, itemsLength: number) {
    return <covet-pics-gallery-item
      class={{ "grid-item": this.layout === "grid", "collage-item": this.layout === "collage" }}
      image-url={item.imageUrl}
      item-source={item.source}
      item-index={idx}
      appStore={this.appStore}
      aria-label={`click on for upload image in gallery, ${this.layout}-item ${idx + 1}/${itemsLength},`}
      role="listitem">
    </covet-pics-gallery-item>;
  }

  private renderDetailUploadItem(item: any, idx: number, itemsLength: number) {
    return <covet-pics-gallery-item-detail
      class={{ "grid-item": this.layout === "grid", "collage-item": this.layout === "collage" }}
      image-url={item.imageUrl}
      item-source={item.source}
      item-index={idx}
      appStore={this.appStore}
      aria-label={`click on for upload image in gallery, ${this.layout}-item ${idx + 1}/${itemsLength},`}
      role="listitem">
    </covet-pics-gallery-item-detail>;
  }

  private renderDetailItem(item: any, idx: number, itemsLength: number) {
    const itemWidth = this.isDesktopScreen 
      ? (100 - (this.settings.item_padding * (this.settings.gallery_photos_per_row - 1))) / this.settings.gallery_photos_per_row
      : (100 - (this.settings.item_mobile_padding * (this.settings.gallery_mobile_photos_per_row - 1))) / this.settings.gallery_mobile_photos_per_row;

    return <covet-pics-gallery-item-detail
      class={{ "grid-item": this.layout === "grid", "collage-item": this.layout === "collage", "highlighted": this.isHighlighted(idx) }}
      highlighted={this.isHighlighted(idx)}
      image-url={getCoverImage(item.cover_images.medium_resolution, item.images.medium_resolution)}
      image-high-resolution-url={getCoverImage(item.cover_images.high_resolution, item.images.high_resolution)}
      image-responsive-sizes={getSizesForResponsiveImage(this.settings.gallery_photos_per_row, this.settings.gallery_mobile_photos_per_row)}
      item-id={item.id}
      item-index={idx}
      item-source={item.source}
      item-type={item.data_type}
      item-width={itemWidth}
      links={item.links}
      url={item.url}
      user-name={item.username}
      caption={item.caption}
      created-time={item.created_time}
      verified={item.verified}
      rating={item.rating !== null ? item.rating : 0}
      role="listitem"
      aria-label={`${this.layout}-item ${idx + 1}/${itemsLength}, post created ${getDateFromTimestamp(item.created_time)}`}
      alt-tag={this.itemImgAltTag(item.alt_tag, item.caption, idx, itemsLength, item.created_time, this.layout)}
      appStore={this.appStore}
    >
    </covet-pics-gallery-item-detail>;
  }

  private renderHeader() {
    return (
      <covet-pics-gallery-header
        product-title={this.productTitle} // TODO remove props drilling from widget, add product title to store
        rating={this.rating} // TODO remove props drilling from widget, add review to store
        breakdown={this.breakdown}
        appStore={this.appStore}
        aria-hidden={`${this.isGridDisabled}`}
      ></covet-pics-gallery-header>
    );
  }

  render() {
    if (typeof this.items !== "undefined" && typeof this.loader !== "undefined") {
      return (
        <Host role="region">
          {this.settings.show_header && this.renderHeader()}
          {this.filtersEnabled && this.renderFilters()}
          <div class={`gallery ${this.layout}`} style={setCovetItemSizeVariables(this.settings.gallery_photos_per_row, this.settings.item_padding, this.settings.gallery_mobile_photos_per_row, this.settings.item_mobile_padding)} 
            role="list" aria-label={`gallery of ${this.items.length} items`}>
            {this.layout === "collage" && <div class="grid-sizer"></div>}
            {this.layout === "collage" && <div class="gutter-sizer"></div>}
            {this.settings.item_style === "standard" && this.items.map((item, idx) => 
              item.source === "upload_widget" 
                ? this.renderStandardUploadItem(item, idx, this.items.length)
                : this.renderStandardItem(item, idx, this.items.length))}
            {this.settings.item_style !== "standard" && this.items.map((item, idx) => 
              item.source === "upload_widget" 
                ? this.renderDetailUploadItem(item, idx, this.items.length)
                : this.renderDetailItem(item, idx, this.items.length))}
          </div>
          {this.renderLoadingAnimation()}
          {this.rederLoadMoreButton()}
        </Host>
      );
    }
  }
}
